import {
  AppointmentTypeEnum,
  InputMaybe,
  Order_By,
  GetFhirAppointmentDiagnosticReportOutput,
  FileOutput,
  useGetSignedUrlFromStorageMutation,
} from 'graphql/generated/hasura';
import {
  CustomClassNamesProps,
  KeyMapProps,
  KeyMapPropValues,
} from 'components/codexTable/interfaces/keymap.interfaces';
import { Record_Order_By } from 'components/codexTable/interfaces/record.interfaces';
import { appointmentRenderInfo } from '../../interfaces';
import { useAppointmentHasuraStatus } from '../upcoming-appointments/helper/getStatusClassName';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import { ReactComponent as Doc } from 'assets/icons/file.svg';
import { AppointmentHasuraStatus } from '../../const/appointment';
import ButtonComponent from 'components/button/buttonComponent';
import TestKitButtonBlock from './TestKitButtonBlock';
import { formatDateForAppointment } from 'utilities/functions';
import { useNavigate } from 'react-router-dom';
import { SCHEDULE_DNA_VISIT } from 'utilities/routes';
import { ReactComponent as EyeIcon } from 'assets/icons/pwEyeShow.svg';
import { useGetFileFromStorage } from '../../../../hooks/useGetFileFromStorage';

export const patientColumns = [
  'date',
  'name',
  'type',
  'status',
  'actions',
] as const;
type Patient_Historic_Appointments_Order_By = Partial<
  Record<(typeof patientColumns)[number], InputMaybe<Order_By>>
>;

export const providerColumns = [
  'date',
  'patientName',
  'type',
  'status',
  'actions',
] as const;
type Provider_Historic_Appointments_Order_By = Partial<
  Record<(typeof providerColumns)[number], InputMaybe<Order_By>>
>;

type Historic_Appointments_Order_By =
  | Patient_Historic_Appointments_Order_By
  | Provider_Historic_Appointments_Order_By;

type ActionType = {
  onClick: (id: string) => void;
  text?: string;
};

const dateColumn: KeyMapPropValues = {
  column: 'date',
  isActive: (orderBy: Record_Order_By) => !!orderBy.date,
  direction: (orderBy: Historic_Appointments_Order_By) =>
    orderBy.date || Order_By.Desc,
  FormatValue: ({ value }) => {
    return (
      <p data-value={value}>
        {formatDateForAppointment(new Date(value as string), {
          simplified: true,
          withTimeZone: true,
        })}
      </p>
    );
  },
};

const patientNameColumn: KeyMapPropValues = {
  column: 'patientName',
  isActive: (orderBy: Record_Order_By) => !!orderBy.name,
  direction: (orderBy: Patient_Historic_Appointments_Order_By) =>
    orderBy.name || Order_By.Desc,
};

const providerNameColumn: KeyMapPropValues = {
  column: 'name',
  isActive: (orderBy: Record_Order_By) => !!orderBy.name,
  direction: (orderBy: Patient_Historic_Appointments_Order_By) =>
    orderBy.name || Order_By.Desc,
};

const typeColumn: KeyMapPropValues = {
  column: 'type',
  isActive: (orderBy: Record_Order_By) => !!orderBy.type,
  direction: (orderBy: Historic_Appointments_Order_By) =>
    orderBy.type || Order_By.Desc,
  FormatValue: ({ value }) => {
    const Icon = appointmentRenderInfo[value as AppointmentTypeEnum].icon;
    const text = appointmentRenderInfo[value as AppointmentTypeEnum].label;
    return (
      <div className="flex items-center" data-value={value}>
        <Icon className="inline" />
        <span className="ml-2">{text}</span>
      </div>
    );
  },
};

const statusColumn: KeyMapPropValues = {
  column: 'status',
  isActive: (orderBy: Record_Order_By) => !!orderBy.status,
  direction: (orderBy: Historic_Appointments_Order_By) =>
    orderBy.status || Order_By.Desc,
  FormatValue: ({ entry }) => {
    const app = entry as Record<string, unknown>;
    const appointmentStatus = (app.appointmentStatus ||
      AppointmentHasuraStatus.Canceled) as AppointmentHasuraStatus;
    const { label, statusClassName } =
      useAppointmentHasuraStatus(appointmentStatus);
    return (
      <span
        className={twMerge('capitalize inline-block', statusClassName)}
        data-value={AppointmentHasuraStatus[appointmentStatus]}
      >
        {label}
      </span>
    );
  },
};

const actionsColumn: KeyMapPropValues = {
  column: 'actions',
  isActive: () => false,
  direction: () => Order_By.Desc,
  FormatValue: ({ value, entry }) => {
    const navigate = useNavigate();
    const [getUrl, { loading: viewLoading }] =
      useGetSignedUrlFromStorageMutation();
    const { downloadFileFromUrl } = useGetFileFromStorage();

    const handleOnView = async (patientPdfId: string) => {
      if (viewLoading) {
        return;
      }
      const { data } = await getUrl({
        variables: { fileId: String(patientPdfId) },
      });
      window.open(data?.GetSignUrlFormStorage?.url, '_blank');
    };

    const handleDetailsClick = () => navigate(SCHEDULE_DNA_VISIT);
    if (!Array.isArray(value)) return null;
    const {
      appointmentStatus,
      id,
      diagnosticReports,
      summaryCompleted,
      type,
      visitSummaryPdfUrl,
    } = entry as Record<string, string>;

    const reports = (
      (diagnosticReports as unknown as GetFhirAppointmentDiagnosticReportOutput[]) ??
      []
    )
      .map(({ patientPdf }) => patientPdf)
      .filter(Boolean);
    const { onClick: onClickSummary } = value[0] as unknown as ActionType;
    const { onClick: onClickTestKit } = value[1] as unknown as ActionType;
    const { onClick: onClickDetails } = value[2] as unknown as ActionType;

    if (type === AppointmentTypeEnum.Dnavisit) {
      return (
        <>
          {[AppointmentHasuraStatus.Canceled].includes(
            appointmentStatus as unknown as AppointmentHasuraStatus,
          ) && (
            <ButtonComponent
              type="underline"
              className="!text-xs !font-bold"
              testID={'dna-detail'}
              onClick={handleDetailsClick}
            >
              {'Details'}
            </ButtonComponent>
          )}
          {[AppointmentHasuraStatus.Completed].includes(
            appointmentStatus as unknown as AppointmentHasuraStatus,
          ) && (
            <div className="flex max-mobile:!block items-center gap-2.5 desktop:py-4">
              {reports.map((rep) => (
                <>
                  <ButtonComponent
                    className="!text-sm font-bold"
                    type="underline"
                    Icon={Doc}
                    iconPosition="left"
                    testID={'eye-icon'}
                    disabled={!visitSummaryPdfUrl}
                    containerClassName="!gap-1.5 max-mobile:justify-start"
                    stroke="stroke-transparent"
                    onClick={() =>
                      downloadFileFromUrl(visitSummaryPdfUrl as string)
                    }
                  >
                    Summary
                  </ButtonComponent>
                  <ButtonComponent
                    className="!text-sm font-bold"
                    type="underline"
                    containerClassName="!gap-1.5 max-mobile:justify-start"
                    Icon={EyeIcon}
                    stroke="stroke-transparent"
                    iconPosition="left"
                    disabled={!rep?.fileId}
                    testID={'download-icon'}
                    onClick={() => handleOnView(rep?.fileId as string)}
                  >
                    Result
                  </ButtonComponent>
                </>
              ))}
            </div>
          )}
        </>
      );
    }

    return (
      <div className="flex items-center gap-2.5">
        {(appointmentStatus as unknown as AppointmentHasuraStatus) ===
        AppointmentHasuraStatus.Completed ? (
          <div className="flex items-center gap-2.5">
            {summaryCompleted ? (
              <ButtonComponent
                type="underline"
                className="!text-xs !font-bold"
                containerClassName="!gap-1"
                onClick={() => onClickSummary(id as string)}
                Icon={Doc}
                iconPosition="left"
                iconWidth="w-[1.25rem] !stroke-transparent"
                testID={`summary-${id}-button`}
              >
                Summary
              </ButtonComponent>
            ) : (
              <ButtonComponent
                type="underline"
                className="!text-xs !font-bold"
                onClick={() => onClickDetails(id as string)}
                testID={`details-${id}-button`}
              >
                Details
              </ButtonComponent>
            )}
            {!!reports.length && (
              <TestKitButtonBlock
                diagnosticReports={reports as FileOutput[]}
                onClickTestKit={onClickTestKit}
                appointmentId={''}
              />
            )}
          </div>
        ) : (
          <ButtonComponent
            type="underline"
            className="!text-xs !font-bold"
            onClick={() => onClickDetails(id as string)}
            testID={`details-${id}-button`}
          >
            Details
          </ButtonComponent>
        )}
      </div>
    );
  },
};

const actionsColumnProvider: KeyMapPropValues = {
  column: 'actions',
  isActive: () => false,
  direction: () => Order_By.Desc,
  FormatValue: ({ value, entry, locale }) => {
    if (!Array.isArray(value)) return null;
    const { id, summaryCompleted, appointmentStatus, diagnosticReports } =
      entry as Record<string, string>;
    const reports = (
      (diagnosticReports as unknown as GetFhirAppointmentDiagnosticReportOutput[]) ??
      []
    )
      .map(({ providerPdf }) => providerPdf)
      .filter(Boolean);
    const { onClick: onClickSummary } = value[0] as unknown as ActionType;
    const { onClick: onClickCompleteSummary, text: completeSummaryText } =
      value[1] as unknown as ActionType;
    const { onClick: onTestKitClick } = value[2] as unknown as ActionType;

    const isShowSummaryAction =
      (appointmentStatus as unknown as AppointmentHasuraStatus) ===
      AppointmentHasuraStatus.Completed;

    return (
      <div className="flex items-center gap-2.5">
        {
          <ButtonComponent
            type="underline"
            className="!text-xs !font-bold"
            onClick={() => onClickSummary(id as string)}
            Icon={Doc}
            iconPosition="left"
            iconWidth="w-[1.25rem] !stroke-transparent"
            testID={`summary-${id}-button`}
          >
            Details
          </ButtonComponent>
        }
        {isShowSummaryAction && !summaryCompleted && (
          <ButtonComponent
            className="!text-xs !font-bold"
            onClick={() => onClickCompleteSummary(id as string)}
            Icon={Doc}
            iconPosition="left"
            iconWidth="w-[1.25rem] !stroke-transparent !fill-current"
            testID={`completeSummary-${id}-button`}
          >
            {completeSummaryText && (locale?.[completeSummaryText] as string)}
          </ButtonComponent>
        )}
        {!!reports.length && (
          <TestKitButtonBlock
            diagnosticReports={reports as FileOutput[]}
            onClickTestKit={onTestKitClick}
            appointmentId={''}
          />
        )}
      </div>
    );
  },
};

export const keyMapPatient: KeyMapProps = {
  date: dateColumn,
  name: providerNameColumn,
  type: typeColumn,
  status: statusColumn,
  actions: actionsColumn,
};

export const keyMapProvider: KeyMapProps = {
  date: dateColumn,
  patientName: patientNameColumn,
  type: typeColumn,
  status: statusColumn,
  actions: actionsColumnProvider,
};

export const customClassesDesktop: CustomClassNamesProps = {
  date: {
    customRowClassName:
      'pl-2 text-left min-w-[200px] align-middle font-medium text-med-gray-3',
    spanClass: 'pl-2',
  },
  name: {
    customRowClassName:
      'text-left min-w-[200px] align-middle font-medium text-med-gray-3',
    spanClass: 'pl-0',
  },
  patientName: {
    customRowClassName:
      'text-left min-w-[200px] align-middle font-medium text-med-gray-3',
    spanClass: 'pl-0',
  },
  type: {
    customRowClassName:
      'text-left align-middle min-w-[140px] font-medium text-med-gray-3',
    spanClass: 'pl-0',
    flexContainerClass: 'justify-start',
  },
  actions: {
    customRowClassName: 'px-0 align-middle min-w-[100px]',
    spanClass: 'pl-0',
  },
  status: {
    customRowClassName: 'px-3 min-w-[6rem] align-middle text-left',
  },
};

export const excludeSort = ['actions', 'logs'];
export const FILTER_BLOCK_ID = 'past-appointments';
